import {
  BodyText,
  Card,
  CardContent,
  DetailText,
  Flex,
  Heading,
} from '@utilitywarehouse/hearth-react';
import {
  GasMediumIcon,
  IconProps,
  LocationMediumIcon,
  PhoneMediumIcon,
  SettingsMediumIcon,
  UserMediumIcon,
} from '@utilitywarehouse/hearth-react-icons';
import MascotInsuranceLight from '@utilitywarehouse/hearth-svg-assets/lib/mascot-insurance-light.svg';
import { FC, ReactNode } from 'react';

import { DISCOUNT_IDS } from '@constants/apiConstants';
import { ChargeAmount, Discount } from '@generated/gql/graphql';
import { isAmountCredit } from '@utils/price';

import BHCPriceHeader from '../BHCPriceHeader';
import { BHCVideo } from './BHCVideo';
import './styles.scss';
import moneyFacts from '/boilerHomeCover/moneyFacts2026.png';

interface BHCHeroProps {
  monthlyCharge: ChargeAmount | undefined;
  price: string;
  discounts?: Discount[];
}

interface BreakdownItemProps {
  Icon: FC<IconProps>;
  children: ReactNode;
}

const BreakdownItem: FC<BreakdownItemProps> = ({ children, Icon }) => (
  <li className="bhc-hero__breakdown-item">
    <Icon />
    <BodyText>{children}</BodyText>
  </li>
);

const hasBHCPromoDiscount = (discounts: Discount[] | undefined): boolean =>
  !!discounts?.find(
    (d) =>
      d.qualified &&
      d.id === DISCOUNT_IDS.INSURANCE_BOILER_THREE_MONTHS_FREE_DISCOUNT &&
      isAmountCredit(d.credit)
  );

const BHCHeroView = ({ monthlyCharge, price, discounts }: BHCHeroProps) => (
  <Flex spacing="xl" direction="column" asChild>
    <div className="bhc-hero__wrapper">
      <div className="h-spacing-xl bhc-hero__Header">
        <Flex direction="column" spacing="lg" asChild>
          <header className="bhc-hero__Heading">
            <Heading as="h1" size="xl" data-testid="bhc-hero-title">
              Boiler & Home Cover
            </Heading>
            {hasBHCPromoDiscount(discounts) ? (
              <BHCPriceHeader
                monthlyCharge={monthlyCharge}
                discounts={discounts}
              />
            ) : (
              <DetailText size="3xl" data-testid="bhc-hero-price">
                {price} <BodyText as="span">a month</BodyText>
              </DetailText>
            )}
          </header>
        </Flex>
        <Card
          variant="subtle"
          colorScheme="insurance"
          className="bhc-hero__Message"
        >
          <CardContent spacing="lg">
            <Flex flex="1 0 80px" display={{ mobile: 'none', tablet: 'flex' }}>
              <img src={MascotInsuranceLight} alt="" />
            </Flex>
            <Flex direction="column" spacing="sm">
              <Heading size="sm">We’ve got you covered</Heading>
              <BodyText>
                Take the hassle out of looking after your property with our five
                star-rated insurance. From your boiler and electronics to
                plumbing and drainage, we'll be there if the unexpected happens.
              </BodyText>
            </Flex>
          </CardContent>
        </Card>
        <div className="bhc-hero__img-wrapper">
          <img
            src={moneyFacts}
            alt="Money Facts logo"
            className="bhc-hero__image"
          />
        </div>
      </div>
      <Flex direction="column" spacing="xl" asChild>
        <div className="bhc-hero__ContentWrapper">
          <Flex asChild spacing="lg" direction="column">
            <ul className="bhc-hero__list">
              <BreakdownItem Icon={GasMediumIcon}>
                Annual boiler service
              </BreakdownItem>
              <BreakdownItem Icon={UserMediumIcon}>
                Unlimited call-outs
              </BreakdownItem>
              <BreakdownItem Icon={PhoneMediumIcon}>
                24/7 UK claims team
              </BreakdownItem>
              <BreakdownItem Icon={SettingsMediumIcon}>
                Parts and labour covered
              </BreakdownItem>
              <BreakdownItem Icon={LocationMediumIcon}>
                Nationwide coverage
              </BreakdownItem>
            </ul>
          </Flex>
          <BHCVideo />
          <BodyText size="sm" color="secondary">
            This product is regulated by the FCA and sold on a non-advised
            basis. This means our Partners are not authorised to answer
            questions about it.
          </BodyText>
        </div>
      </Flex>
    </div>
  </Flex>
);

const BHCHero = ({ monthlyCharge, price, discounts }: BHCHeroProps) => {
  return (
    <BHCHeroView
      monthlyCharge={monthlyCharge}
      price={price}
      discounts={discounts}
    />
  );
};

export { BHCHero };
